import { BookOpen, Clock, User, FileDown } from "lucide-react";
import { Header } from "@/components/Header";
import { LeadForm } from "@/components/LeadForm";
import { ConsultantCard } from "@/components/ConsultantCard";
import { Faq } from "@/components/Faq";
import { CtaFinal } from "@/components/CtaFinal";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useSeo, faqSchema, MESE_ANNO } from "@/lib/seo";
import { FAQ_GUIDE, CONSULTANT, waLink } from "@/data/content";
import { scrollToId } from "@/lib/smooth";

const TOC = [
  { id: "guida-deducibilita", label: "Come funziona la deducibilità" },
  { id: "guida-percentuali", label: "Le percentuali per categoria" },
  { id: "guida-iva", label: "Detraibilità IVA: 40% o 100%" },
  { id: "guida-tabella", label: "Tabella riepilogativa 2026" },
  { id: "guida-limiti", label: "I limiti da conoscere" },
  { id: "guida-documenti", label: "Documenti da conservare" },
];

const TABLE_ROWS = [
  { profilo: "Professionisti e ditte individuali", ded: "20%", limite: "€ 3.615,20 / anno", iva: "40%" },
  { profilo: "Agenti e rappresentanti di commercio", ded: "80%", limite: "€ 5.164,57 / anno", iva: "100%" },
  { profilo: "Uso promiscuo ai dipendenti", ded: "70%", limite: "nessun limite", iva: "40% (100% se strumentale)" },
  { profilo: "Veicoli esclusivamente strumentali", ded: "100%", limite: "nessun limite", iva: "100%" },
];

const P = ({ children }) => <p className="mt-4 text-base leading-relaxed text-xmove-muted">{children}</p>;
const H2 = ({ id, children }) => (
  <h2 id={id} className="mt-14 scroll-mt-28 font-display text-2xl font-bold text-xmove-ink sm:text-3xl">{children}</h2>
);
const H3 = ({ children }) => <h3 className="mt-8 font-display text-xl font-bold text-xmove-ink">{children}</h3>;

export default function GuidePage() {
  useSeo({
    title: `Deducibilità Noleggio Auto Partita IVA ${new Date().getFullYear()}: Guida Completa | XMOVE`,
    description:
      "Noleggio lungo termine e partita IVA: percentuali di deduzione (art. 164 TUIR), detraibilità IVA al 40% o 100%, limiti e tabella riepilogativa aggiornata.",
    path: "/guide/deducibilita-noleggio-auto-partita-iva/",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: "Deducibilità e detraibilità del noleggio auto per partite IVA",
        author: { "@type": "Person", name: CONSULTANT.name },
        publisher: { "@type": "Organization", name: "XMOVE Italia" },
        datePublished: "2026-07-01",
      },
      faqSchema(FAQ_GUIDE),
    ],
  });

  return (
    <div className="pb-[72px] md:pb-0" data-testid="guide-page">
      <Header />
      <main>
        <section className="border-b border-xmove-ink/8 bg-white" data-testid="guide-hero">
          <div className="mx-auto max-w-3xl px-5 py-14 lg:py-20">
            <Reveal>
              <p className="inline-flex items-center gap-2 rounded-full border border-xmove-orange/25 bg-xmove-orange/8 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-xmove-orange">
                <BookOpen size={13} /> Guida fiscale · Aggiornata a {MESE_ANNO}
              </p>
              <h1 className="mt-6 text-balance font-display text-4xl font-bold leading-[1.08] text-xmove-ink sm:text-5xl" data-testid="guide-h1">
                Deducibilità del noleggio auto per partite IVA: la guida completa
              </h1>
              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-xmove-muted">
                <span className="flex items-center gap-1.5"><User size={15} className="text-xmove-orange" /> {CONSULTANT.name}</span>
                <span className="flex items-center gap-1.5"><Clock size={15} className="text-xmove-orange" /> 8 min di lettura</span>
              </div>
            </Reveal>
          </div>
        </section>

        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-12 lg:grid-cols-[260px_1fr] lg:px-8 lg:py-16">
          <aside className="lg:sticky lg:top-28 lg:self-start" data-testid="guide-toc">
            <p className="text-xs font-bold uppercase tracking-widest text-xmove-muted">In questa guida</p>
            <nav className="mt-4 flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0">
              {TOC.map((t) => (
                <button
                  key={t.id}
                  onClick={() => scrollToId(t.id)}
                  data-testid={`toc-${t.id}`}
                  className="shrink-0 rounded-full border border-xmove-ink/12 px-4 py-2 text-left text-xs font-semibold text-xmove-ink/75 transition-colors hover:border-xmove-orange hover:text-xmove-orange lg:rounded-lg lg:border-0 lg:px-3 lg:text-sm"
                >
                  {t.label}
                </button>
              ))}
            </nav>
          </aside>

          <article className="max-w-3xl" data-testid="guide-body">
            <Reveal>
              <P>
                <b className="text-xmove-ink">Quanto si scarica davvero dal noleggio a lungo termine?</b> È la
                prima domanda che ci fanno professionisti, titolari di ditta e agenti di commercio. La risposta
                breve: dipende dal tuo profilo fiscale, e la differenza tra una categoria e l'altra vale migliaia
                di euro l'anno. La risposta lunga è questa guida, aggiornata alla normativa vigente.
              </P>
            </Reveal>

            <Reveal>
              <H2 id="guida-deducibilita">Come funziona la deducibilità del noleggio lungo termine</H2>
              <P>
                Il riferimento normativo è l'articolo 164 del TUIR, che fissa limiti e percentuali di deduzione
                per i veicoli a motore utilizzati nell'attività d'impresa o professionale. Nel noleggio a lungo
                termine il costo fiscalmente rilevante è il canone, comprensivo della quota "auto" e dei servizi
                accessori (assicurazioni, manutenzione, assistenza).
              </P>
              <P>
                Un dettaglio che molti ignorano: l'eventuale anticipo versato all'inizio del contratto non si
                deduce tutto nell'anno del pagamento, ma va ripartito sulla durata del contratto e concorre allo
                stesso limite annuo previsto per i canoni.
              </P>
            </Reveal>

            <Reveal>
              <H2 id="guida-percentuali">Le percentuali di deduzione per categoria</H2>
              <H3>Professionisti e ditte individuali: 20%</H3>
              <P>
                Per la generalità dei lavoratori autonomi il canone è deducibile al 20%, fino a un costo annuo
                massimo di € 3.615,20. In pratica, se il tuo canone annuo supera questa soglia, la parte
                eccedente resta comunque indeducibile.
              </P>
              <H3>Agenti e rappresentanti di commercio: 80%</H3>
              <P>
                Categoria storicamente avvantaggiata perché l'auto è considerata strumento essenziale
                dell'attività: deduzione dell'80% con limite annuo di € 5.164,57. Per un agente che percorre
                40.000 km l'anno, il noleggio è quasi sempre la formula fiscalmente più efficiente.
              </P>
              <H3>Uso promiscuo ai dipendenti: 70%</H3>
              <P>
                Se l'azienda assegna l'auto a un dipendente con uso promiscuo (lavoro e privato) per la
                maggior parte del periodo d'imposta, il costo è deducibile al 70% senza limiti massimi di
                importo. Per il dipendente si genera un fringe benefit tassato secondo le regole vigenti.
              </P>
              <H3>Veicoli esclusivamente strumentali: 100%</H3>
              <P>
                Quando il veicolo è indispensabile e ad uso esclusivo dell'attività (ad esempio i veicoli dei
                tassisti, delle autoscuole o adibiti a uso pubblico), la deduzione sale al 100%.
              </P>
            </Reveal>

            <Reveal>
              <H2 id="guida-iva">Detraibilità IVA: 40% o 100%</H2>
              <P>
                L'IVA sui canoni di noleggio segue regole proprie, indipendenti dalla deducibilità del costo:
                la detrazione ordinaria è del 40% per professionisti e imprese, salvo i casi in cui l'auto è
                utilizzata esclusivamente nell'attività.
              </P>
              <P>
                Si arriva al 100% di detrazione per agenti e rappresentanti di commercio, per i veicoli
                strumentali puri e per quelli adibiti a uso pubblico (taxi, NCC, autoscuole). Il 40% residuo,
                quando non detraibile, diventa un costo che concorre anch'esso alla deducibilità nei limiti
                dell'art. 164.
              </P>
            </Reveal>

            <Reveal>
              <H2 id="guida-tabella">Tabella riepilogativa {new Date().getFullYear()}</H2>
              <div className="mt-6 overflow-hidden rounded-2xl border border-xmove-ink/8 bg-white shadow-soft" data-testid="guide-table">
                <div className="hidden grid-cols-4 gap-4 border-b border-xmove-ink/8 bg-xmove-cream px-6 py-4 text-xs font-bold uppercase tracking-wider text-xmove-muted md:grid">
                  <span>Profilo</span><span>Costo deducibile</span><span>Limite annuo</span><span>IVA detraibile</span>
                </div>
                {TABLE_ROWS.map((r) => (
                  <div key={r.profilo} className="grid grid-cols-2 gap-x-4 gap-y-2 border-b border-xmove-ink/6 px-6 py-5 last:border-0 md:grid-cols-4">
                    <span className="col-span-2 text-sm font-bold text-xmove-ink md:col-span-1">{r.profilo}</span>
                    <span className="text-sm"><b className="text-xmove-orange">{r.ded}</b></span>
                    <span className="text-sm text-xmove-muted">{r.limite}</span>
                    <span className="col-span-2 text-sm md:col-span-1"><b className="text-xmove-green">{r.iva}</b></span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-xmove-muted" data-testid="tax-disclaimer">*Tabella puramente indicativa: consultare sempre il proprio commercialista.</p>
            </Reveal>

            <Reveal>
              <div className="mt-14 rounded-2xl border-2 border-xmove-orange/30 bg-xmove-orange/5 p-6 sm:p-8" data-testid="guide-lead-magnet">
                <h2 className="font-display text-xl font-bold text-xmove-ink sm:text-2xl">
                  <FileDown size={20} className="mr-2 inline text-xmove-orange" />
                  Simulazione fiscale gratuita sul tuo caso
                </h2>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-xmove-muted">
                  Ogni profilo è diverso: regime fiscale, chilometraggio, categoria. Lasciaci i contatti e ti
                  prepariamo una simulazione personalizzata di deducibilità e detraibilità da portare al tuo
                  commercialista. Gratuita, ti richiamiamo in pochi minuti.
                </p>
                <div id="preventivo" className="mt-6 scroll-mt-28 rounded-2xl bg-white p-5 shadow-soft sm:p-6">
                  <LeadForm idPrefix="guide" compact />
                </div>
              </div>
            </Reveal>

            <Reveal>
              <H2 id="guida-limiti">I limiti da conoscere prima di firmare</H2>
              <P>
                Primo: il limite annuo (€ 3.615,20 per i professionisti) comprende canoni e quota di anticipo
                spalmata — scegliere un anticipo alto non "sblocca" più deduzione. Secondo: le percentuali si
                applicano al costo effettivamente sostenuto nell'anno, quindi contratti che iniziano a metà
                anno producono mezza deduzione. Terzo: per l'uso promiscuo ai dipendenti serve documentare
                l'assegnazione, altrimenti il beneficio decade.
              </P>
              <P>
                Infine, attenzione alla contabilità: i canoni di noleggio si registrano tra i servizi, non tra
                i beni ammortizzabili. Niente quote di ammortamento, niente cespite in bilancio — uno dei
                motivi per cui molte SRL preferiscono il noleggio all'acquisto o al leasing finanziario.
              </P>
            </Reveal>

            <Reveal>
              <H2 id="guida-documenti">Documenti da conservare</H2>
              <P>
                Per stare tranquillo in caso di verifica: contratto di noleggio completo di condizioni,
                fatture mensili del noleggiatore, eventuale lettera di assegnazione al dipendente per l'uso
                promiscuo e, per gli agenti di commercio, la documentazione che attesta la natura
                dell'attività. Con XMOVE ricevi un fascicolo digitale ordinato pronto per il commercialista.
              </P>
              <P>
                <b className="text-xmove-ink">Hai un caso particolare?</b> Regimi forfettari, società di
                persone, flotte miste: scrivici su WhatsApp o compila il modulo qui sopra — il confronto con
                un consulente mobility è gratuito e senza impegno.
              </P>
            </Reveal>
          </article>
        </div>

        <ConsultantCard />
        <Faq faqs={FAQ_GUIDE} title="Domande frequenti sulla deducibilità" />
        <CtaFinal
          title="Vuoi i numeri esatti sul tuo profilo fiscale?"
          subtitle="Simulazione di deducibilità e detraibilità IVA gratuita, preparata da un consulente mobility in pochi minuti."
        />
      </main>
      <Footer />
      <MobileStickyBar waText={waLink("Salve XMOVE, ho letto la guida sulla deducibilità: vorrei una simulazione fiscale per la mia partita IVA.")} />
    </div>
  );
}
